import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Tooltip,
  useTheme,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import {
  CloseRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
} from "@mui/icons-material";
import { getPipelineLogs } from "../../../../../core/repo/sessionRepo";
import Loader from "../../../../../components/Loader";
import { getIsDebugModeOn } from "../../../../../core/storage/localStorage";
import RenderMarkdown from "./Answer/RenderMarkdown";
import { usePostHog } from "posthog-js/react";
import {
  OPEN_DEBUG_DIALOG,
  CLOSE_DEBUG_DIALOG,
  CLICK_MAIN_TAB_ON_DEBUG_DIALOG,
  CLICK_SUB_TAB_ON_DEBUG_DIALOG,
} from "../../../../../utils/posthogEvents";

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: "100%",
  },
}));

// const labels = {
//   query_intent: "Query Intent",
//   citation: "Citation",
//   similar_chunks: "Similar Chunks",
//   reranker: "Reranker",
//   llm: "LLM",
// };

function Debug({ query }) {
  const posthog = usePostHog();
  const { model } = query;
  const theme = useTheme();
  const [isDebugDialogOpen, setIsDebugDialogOpen] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(() => getIsDebugModeOn());

  useEffect(() => {
    const handleStorage = () => {
      const isDebugMode = getIsDebugModeOn();
      setIsDebugMode(isDebugMode);
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const handleOpen = () => {
    setIsDebugDialogOpen(true);
    posthog.capture(OPEN_DEBUG_DIALOG);
  };

  const handleClose = () => {
    setIsDebugDialogOpen(false);
    posthog.capture(CLOSE_DEBUG_DIALOG);
  };

  return (
    <Box>
      {process.env.REACT_APP_ENV === "staging" && isDebugMode && (
        <Tooltip title={"Answered using: " + model}>
          <IconButton onClick={handleOpen} sx={{ marginLeft: 1 }} size="small">
            <img
              src={
                model === "gpt-4o"
                  ? "/images/chatgpt_logo.png"
                  : "/images/claude_ai_icon.svg"
              }
              alt="model_name"
              height={18}
              width={18}
              style={{ color: theme.palette.text.disabled }}
            />
          </IconButton>
        </Tooltip>
      )}
      {isDebugDialogOpen && (
        <DebugDialog query={query} handleClose={handleClose} />
      )}
    </Box>
  );
}

function DebugDialog({ query, handleClose }) {
  const posthog = usePostHog();
  const { model, message_id: questionId } = query;
  const { classes } = useStyles();
  const [steps, setSteps] = useState([]);
  const [selectedStep, setSelectedStep] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [groupedSteps, setGroupedSteps] = useState({});
  const [selectedMainTab, setSelectedMainTab] = useState(null);
  const [selectedSubTab, setSelectedSubTab] = useState(null);

  // Function to group steps by name and handle duplicates
  const groupSteps = (stepsData) => {
    const grouped = {};
    stepsData.forEach((step, index) => {
      const stepName = step.step;
      if (!grouped[stepName]) {
        grouped[stepName] = [];
      }
      grouped[stepName].push({ ...step, originalIndex: index });
    });
    return grouped;
  };

  async function fetchPipelineLogs() {
    setIsLoading(true);
    try {
      const response = await getPipelineLogs(questionId);
      setSteps(response || []);

      if (response && response.length > 0) {
        const grouped = groupSteps(response);
        setGroupedSteps(grouped);

        // Set initial selection
        const firstMainTab = Object.keys(grouped)[0];
        setSelectedMainTab(firstMainTab);
        setSelectedSubTab(0); // First sub-tab
        setSelectedStep(grouped[firstMainTab][0]);
      }
    } catch (err) {
      console.log(err);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchPipelineLogs();
  }, []);

  const handleSelectMainTab = (mainTabName) => {
    setSelectedMainTab(mainTabName);
    setSelectedSubTab(0); // Reset to first sub-tab
    setSelectedStep(groupedSteps[mainTabName][0]);
    posthog.capture(CLICK_MAIN_TAB_ON_DEBUG_DIALOG, {
      mainTabName: mainTabName,
    });
  };

  const handleSelectSubTab = (subTabIndex, subStep) => {
    setSelectedSubTab(subTabIndex);
    setSelectedStep(subStep);
    posthog.capture(CLICK_SUB_TAB_ON_DEBUG_DIALOG, {
      subTabIndex: subTabIndex,
    });
  };

  return (
    <Dialog
      onClose={handleClose}
      open={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen
      classes={{ paper: classes.root }}>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          boxShadow:
            "0 0 1px 0 rgba(0,0,0,0.31), 0 4px 6px -2px rgba(0,0,0,0.25)",
        }}>
        <IconButton edge="start" onClick={handleClose} sx={{ mr: 1 }}>
          <CloseRounded />
        </IconButton>
        <Typography variant="h6">Debug</Typography>
        <Box mr={1.5} />
        <Tooltip title={model}>
          <img
            src={
              model === "gpt-4o"
                ? "/images/chatgpt_logo.png"
                : "/images/claude_ai_icon.svg"
            }
            alt="mode_name"
            height={32}
            width={32}
          />
        </Tooltip>
      </DialogTitle>
      <DialogContent sx={{ overflow: "hidden" }}>
        <Box display={"flex"} flexDirection={"column"} height={"100%"}>
          {/* Main Tabs */}
          <Box display="flex" alignItems={"center"} py={2}>
            {Object.keys(groupedSteps).map((mainTabName) => (
              <Chip
                key={mainTabName}
                variant={
                  selectedMainTab === mainTabName ? "contained" : "outlined"
                }
                label={mainTabName}
                sx={{ marginRight: 2, cursor: "pointer" }}
                onClick={() => {
                  handleSelectMainTab(mainTabName);
                }}
              />
            ))}
          </Box>

          {/* Sub Tabs - only show if there are multiple items for the selected main tab */}
          {selectedMainTab &&
            groupedSteps[selectedMainTab] &&
            groupedSteps[selectedMainTab].length > 1 && (
              <Box
                display="flex"
                alignItems={"center"}
                py={1}
                sx={{ borderTop: "1px solid #e0e0e0" }}>
                {groupedSteps[selectedMainTab].map((subStep, index) => (
                  <Chip
                    key={`${selectedMainTab}-${index}`}
                    variant={
                      selectedSubTab === index ? "contained" : "outlined"
                    }
                    label={`${selectedMainTab} ${index + 1}`}
                    size="small"
                    sx={{ marginRight: 1, cursor: "pointer" }}
                    onClick={() => {
                      handleSelectSubTab(index, subStep);
                    }}
                  />
                ))}
              </Box>
            )}
          {selectedStep && (
            <Box flex={1} overflow={"auto"}>
              <Display text={selectedStep["input"]} label={"Input"} />
              <Box mb={3} />
              <Display text={selectedStep["output"]} label={"Output"} />
            </Box>
          )}
        </Box>
        {steps.length === 0 && !isLoading && (
          <Typography>No data to show</Typography>
        )}
        {isLoading && <Loader />}
      </DialogContent>
    </Dialog>
  );
}

function Display({ text, label }) {
  const [open, setOpen] = useState(true);

  // Function to check if content appears to be MMD (contains markdown syntax)
  const isMMDContent = (content) => {
    if (typeof content !== "string") return false;
    // Comprehensive heuristic to detect markdown-like content including mathpix
    return (
      // Standard markdown
      content.includes("##") ||
      content.includes("**") ||
      content.includes("*") ||
      content.includes("[") ||
      content.includes("]") ||
      content.includes("`") ||
      content.includes("\n- ") ||
      content.includes("\n1. ") ||
      // Mathpix/LaTeX specific patterns
      content.includes("\\begin{") ||
      content.includes("\\end{") ||
      content.includes("\\cite{") ||
      content.includes("\\hline") ||
      content.includes("\\multicolumn") ||
      content.includes("\\cline") ||
      content.includes("metadata=") ||
      // More comprehensive checks
      content.match(/\\\w+{/) || // LaTeX commands
      content.includes("tabular") ||
      content.includes("\\\\") // LaTeX line breaks
    );
  };

  // Function to extract content for MMD rendering
  const extractMMDContent = (content) => {
    // Case 1: Direct string content
    if (typeof content === "string") {
      return content;
    }

    // Case 2: Object with potential MMD in values
    if (content && typeof content === "object" && !Array.isArray(content)) {
      // Look for common keys that might contain MMD content, including source_text (renamed from beg_text)
      const possibleKeys = [
        "chunk",
        "content",
        "text",
        "message",
        "mmd",
        "source_text",
      ];
      for (const key of possibleKeys) {
        if (content[key] && typeof content[key] === "string") {
          return content[key];
        }
      }
      // If no specific key found, try the first string value
      const firstStringValue = Object.values(content).find(
        (v) => typeof v === "string"
      );
      if (firstStringValue) return firstStringValue;
    }

    return null;
  };

  // Function to format the text content
  const formatText = (content) => {
    if (typeof content === "string") {
      // Replace both literal \n and escaped \\n with actual line breaks
      return content.replace(/\\n/g, "\n");
    }
    // For objects, convert to string first, then handle newlines
    const jsonString = JSON.stringify(content, null, 2);
    return jsonString.replace(/\\n/g, "\n");
  };

  // Function to preprocess mathpix LaTeX content
  const preprocessMathpixContent = (content) => {
    return (
      content
        // Convert LaTeX section commands to markdown headings
        .replace(/\\section\*?\{([^}]*)\}/g, "# $1") // Section to H1
        .replace(/\\subsection\*?\{([^}]*)\}/g, "## $1") // Subsection to H2
        .replace(/\\subsubsection\*?\{([^}]*)\}/g, "### $1") // Subsubsection to H3
        .replace(/\\paragraph\*?\{([^}]*)\}/g, "#### $1") // Paragraph to H4
        .replace(/\\subparagraph\*?\{([^}]*)\}/g, "##### $1") // Subparagraph to H5
        // Clean up LaTeX table syntax that might not render well
        .replace(/\\begin\{tabular\}[^}]*\}/g, "") // Remove tabular begin
        .replace(/\\end\{tabular\}/g, "") // Remove tabular end
        .replace(/\\hline/g, "") // Remove horizontal lines
        .replace(/\\cline\{[^}]*\}/g, "") // Remove column lines
        .replace(/\\multicolumn\{[^}]*\}\{[^}]*\}\{([^}]*)\}/g, "$1") // Extract multicolumn content
        .replace(/\\\\/g, "\n") // Convert LaTeX line breaks to newlines
        .replace(/\\cite\{[^}]*\}/g, "") // Remove citations
        .replace(/metadata=\{[^}]*\}/g, "") // Remove metadata
        // Convert other common LaTeX commands
        .replace(/\\textbf\{([^}]*)\}/g, "**$1**") // Bold text
        .replace(/\\textit\{([^}]*)\}/g, "*$1*") // Italic text
        .replace(/\\emph\{([^}]*)\}/g, "*$1*") // Emphasis
        // Clean up extra whitespace
        .replace(/\n\s*\n/g, "\n") // Remove extra blank lines
        .replace(/^\s+|\s+$/g, "")
    ); // Trim start and end
  };

  // Function to render content (MMD or regular)
  const renderContent = (content) => {
    const mmdContent = extractMMDContent(content);

    if (mmdContent && isMMDContent(mmdContent)) {
      // Preprocess mathpix content before rendering
      const processedContent = preprocessMathpixContent(mmdContent);

      // Render as MMD with monospace font and caption size
      return (
        <Box
          sx={{
            fontFamily: "monospace",
            fontSize: "12px",
            lineHeight: 1.4,
            "& *": {
              fontFamily: "monospace !important",
              fontSize: "12px !important",
            },
          }}>
          <RenderMarkdown message={processedContent} />
        </Box>
      );
    }

    // Fallback to regular pre formatting
    return (
      <pre
        style={{
          whiteSpace: "pre-wrap",
          margin: 0,
          fontFamily: "monospace",
          fontSize: "12px",
          lineHeight: 1.4,
        }}>
        {formatText(content)}
      </pre>
    );
  };

  // Function to try parsing JSON strings
  const tryParseJSON = (content) => {
    if (typeof content !== "string") return content;

    // Check if it looks like a JSON string
    const trimmed = content.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return JSON.parse(content);
      } catch (e) {
        // If parsing fails, return original content
        return content;
      }
    }
    return content;
  };

  // Function to transform keys (e.g., beg_text to source_text)
  const transformKey = (key) => {
    if (key === "beg_text") {
      return "source_text";
    }
    return key;
  };

  // Function to render JSON items as separate containers
  const renderJsonItems = (content) => {
    // Try to parse JSON strings first
    const parsedContent = tryParseJSON(content);

    // If it's an array, render each item
    if (Array.isArray(parsedContent)) {
      return parsedContent.map((item, index) => (
        <Box
          key={index}
          sx={{
            backgroundColor: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            p: 1,
            mb: 1,
          }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: "bold", color: "#666" }}>
            Item {index}
          </Typography>
          <Box sx={{ mt: "4px" }}>{renderContent(item)}</Box>
        </Box>
      ));
    }

    // If it's an object, render each key-value pair
    if (parsedContent && typeof parsedContent === "object") {
      return Object.entries(parsedContent).map(([key, value]) => {
        const displayKey = transformKey(key);
        return (
          <Box
            key={key}
            sx={{
              backgroundColor: (theme) => theme.palette.background.light,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 1,
              mb: 1,
            }}>
            <Typography variant="caption" sx={{ fontWeight: "bold" }}>
              {displayKey}
            </Typography>
            <Box sx={{ mt: "4px" }}>{renderContent(value)}</Box>
          </Box>
        );
      });
    }

    // For primitive values, use regular formatting
    return renderContent(parsedContent);
  };

  return (
    <Box>
      <Box display={"flex"} alignItems={"center"}>
        <Typography variant="subtitle2">{label}</Typography>
        <IconButton sx={{ marginLeft: 1 }} onClick={() => setOpen(!open)}>
          {open ? (
            <ExpandLessRounded sx={{ fontSize: 16 }} />
          ) : (
            <ExpandMoreRounded sx={{ fontSize: 16 }} />
          )}
        </IconButton>
      </Box>
      {open && (
        <Box
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1,
            mt: 1,
          }}>
          {renderJsonItems(text)}
        </Box>
      )}
    </Box>
  );
}

export default Debug;
